import React, { useRef, useState, useEffect } from "react";
import { PieChartProps } from "./types";
import { PieChartSvg } from "./pie-chart";

export const PieChart = <DataType,>({ 
  minHeight = 250, 
  minWidth = 200,
  maxHeight,
  ...props 
}: PieChartProps<DataType>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const { width, height } = container.getBoundingClientRect();
      let finalHeight = Math.max(height, minHeight);
      if (maxHeight) finalHeight = Math.min(finalHeight, maxHeight);
      setDimensions({ 
        width: Math.max(width, minWidth), 
        height: finalHeight 
      });
    };

    // Initial measurement after layout
    updateDimensions();

    // Set up ResizeObserver to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [minHeight, minWidth, maxHeight]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        minHeight: `${minHeight}px`, 
        maxHeight: maxHeight ? `${maxHeight}px` : undefined,
        height: maxHeight ? `${maxHeight}px` : '100%' 
      }}
    >
      {dimensions.width > 0 && (
        <PieChartSvg 
          {...props} 
          width={dimensions.width} 
          height={dimensions.height} 
        />
      )}
    </div>
  );
};
